import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../../components/ui/Layout';
import Table from '../../components/ui/Table';
import ActionDropdown from '../../components/ui/ActionDropdown';
import ActionButtonsRow from '../../components/ui/ActionButtonsRow';
import SearchFilterCard, { FilterConfig } from '../../components/ui/SearchFilterCard';
import BulkActionsBar from '../../components/ui/BulkActionsBar';
import {
  RiShoppingBag3Line,
  RiArchiveLine,
  RiRefreshLine,
  RiEyeLine,
  RiEditLine,
  RiDeleteBinLine,
  RiDeleteBin2Line,
  RiFileDownloadLine,
} from 'react-icons/ri';
import productService, { Product } from '../../services/productService';
import { showAlert, showConfirm } from '../../utils/alert';
import { getApiErrorMessageAsync } from '../../utils/mapFieldErrors';
import { PAGINATION_CONFIG } from '../../config/pagination';
import Pagination from '../../components/ui/Pagination';
import { useUserPageSize } from '../../hooks/useUserPageSize';
import { formatDateTimeFull, getLocalDateString } from '../../utils/dateUtils';
import { downloadExcelFile } from '../../utils/downloadExcel';
import { usePermission } from '../../hooks/usePermission';
import LoadingOverlayModal from '../../components/ui/LoadingOverlayModal';
import { useClampPage } from '../../hooks/useClampPage';
import { runBulkDelete, bulkDeleteFailureMessage } from '../../utils/bulkDelete';

import { useLatestRequest } from '../../hooks/useLatestRequest';
import { detailNavState } from '../../components/detail/detailNavigation';
import { PRODUCT_NEW_URL, productDetailUrl, productEditUrl } from '../../utils/productLinks';

/**
 * หน้ารายการสินค้า — ดู/เพิ่ม/แก้ไขเป็นหน้าเต็มแล้ว (มติ PO 28 ต.ค. 2026 · ProductViewPage / ProductFormPage)
 * ไปหน้าเต็มพร้อม state.from = URL รายการนี้ (ตัวกรอง/หน้าไม่หายเมื่อกดกลับ) · ลิงก์เก่า ?view=/?edit= → LegacyDetailRedirect ใน App.tsx
 * ลบ (confirm) / ลบหลายรายการ ยังทำจากหน้ารายการได้
 */
const ProductPage: React.FC = () => {
  const { canCreate, canEdit, canDelete, canExport } = usePermission();
  const navigate = useNavigate();
  const location = useLocation();
  const openProductPage = (path: string) => navigate(path, { state: detailNavState(location) });
  // S1114 URL-sync: read URL params for lazy init (filter+page bidirectional)
  const [searchParams, setSearchParams] = useSearchParams();
  const urlPageParam = searchParams.get('page');
  const urlStatusParam = searchParams.get('status') || '';
  // State สำหรับ pagination และ search
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(() => {
    if (!urlPageParam) return 1;
    const n = parseInt(urlPageParam, 10);
    if (!Number.isFinite(n) || n < 1) return 1;
    return Math.min(n, 9999);
  });
  const [pageSize, setPageSize] = useState<number>(PAGINATION_CONFIG.DEFAULT_PAGE_SIZE);
  useUserPageSize(pageSize, setPageSize);

  // State สำหรับ filters
  // S1114 URL-sync: hydrate statusFilter from URL
  const [statusFilter, setStatusFilter] = useState<string>(
    urlStatusParam === 'active' || urlStatusParam === 'inactive' ? urlStatusParam : ''
  );

  // S1114 URL-sync: write filter+page to URL (Variant A overwrite — no other URL params)
  const syncFiltersToUrl = useCallback((status: string, page: number) => {
    const newParams = new URLSearchParams();
    if (status === 'active' || status === 'inactive') newParams.set('status', status);
    if (page > 1) newParams.set('page', String(page));
    setSearchParams(newParams, { replace: true });
  }, [setSearchParams]);

  useEffect(() => {
    syncFiltersToUrl(statusFilter, currentPage);
  }, [statusFilter, currentPage, syncFiltersToUrl]);
  
  // State สำหรับข้อมูลสินค้า (แทน React Query)
  const [products, setProducts] = useState<Product[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  
  // State สำหรับ loading แต่ละ action
  const [isSyncing, setIsSyncing] = useState<boolean>(false);

  const [selectedProducts, setSelectedProducts] = useState<number[]>([]);
  const [bulkLoading, setBulkLoading] = useState<boolean>(false);
  
  // ฟังก์ชันสำหรับดึงข้อมูลสินค้า
  const beginRequest = useLatestRequest();  // BUG-S2268-002: response ช้าทับของใหม่
  const fetchProducts = useCallback(async () => {
    const isStale = beginRequest();
    setIsLoading(true);
    setError(null);
    try {
      const params: any = {
        page: currentPage,
        limit: pageSize
      };
      
      // เพิ่มเฉพาะ parameter ที่มีค่า
      if (searchTerm) {
        params.search = searchTerm;
      }

      if (statusFilter) {
        params.is_active = statusFilter === 'active';
      }
      const response = await productService.getProducts(params);
      if (isStale()) return;
      setProducts(response.items || []);
      setTotalItems(response.total || 0);
    } catch (err: any) {
      if (isStale()) return;
      console.error('Error fetching products:', err);
      setError('ไม่สามารถโหลดข้อมูลสินค้าได้');
      showAlert('เกิดข้อผิดพลาด', 'ไม่สามารถโหลดข้อมูลสินค้าได้', 'error');
    } finally {
      if (!isStale()) setIsLoading(false);
    }
  }, [currentPage, pageSize, searchTerm, statusFilter, beginRequest]);

  // โหลดข้อมูลเมื่อ component mount หรือ dependencies เปลี่ยน
  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);
  
  // Loading state รวม
  const loading = isLoading || isSyncing || bulkLoading;
  useClampPage(currentPage, totalItems, pageSize, setCurrentPage, isLoading);
  
  // ฟังก์ชันสำหรับลบสินค้า
  const handleDeleteProduct = (product: Product) => {
    showConfirm(
      'ยืนยันการลบสินค้า',
      `คุณต้องการลบสินค้า ${product.name} ใช่หรือไม่? การกระทำนี้ไม่สามารถเปลี่ยนกลับได้ และอาจส่งผลกระทบต่อข้อมูลที่เกี่ยวข้อง`,
      async () => {
        try {
          // แสดง loading ระหว่างลบ
          setIsLoading(true);
          
          // เรียก API ลบโดยตรง
          const result = await productService.deleteProduct(product.id);
          
          // แสดงข้อความสำเร็จ
          if (result.message) {
            showAlert('สำเร็จ', result.message, 'success');
          }
          
          // ดึงข้อมูลใหม่ทันที
          await fetchProducts();
          
        } catch (error: any) {
          console.error('Delete error:', error);
          
          // แสดง error message
          let errorMessage = 'เกิดข้อผิดพลาดในการลบสินค้า';
          if (error.response?.status === 404) {
            errorMessage = 'สินค้าที่ต้องการลบไม่มีอยู่ในระบบแล้ว';
            // ถ้า 404 ก็ยังต้อง refresh เพื่อลบออกจากหน้าจอ
            await fetchProducts();
          } else if (error.response?.data?.error?.user_message) {
            errorMessage = error.response.data.error.user_message;
          } else if (error.message) {
            errorMessage = error.message;
          }
          
          showAlert('เกิดข้อผิดพลาด', errorMessage, 'error');
        } finally {
          setIsLoading(false);
        }
      },
      { isDanger: true, confirmText: 'ลบ', cancelText: 'ยกเลิก' }
    );
  };
  
  // ฟังก์ชันสำหรับซิงค์ข้อมูลสินค้า
  const handleSyncProducts = () => {
    showConfirm(
      'ยืนยันการซิงค์ข้อมูล',
      'คุณต้องการซิงค์ข้อมูลสินค้าจาก Flow Account ใช่หรือไม่?',
      async () => {
        try {
          setIsSyncing(true);
          
          // เรียก API sync โดยตรง
          await productService.syncFromFlowAccount();
          showAlert('สำเร็จ', 'ซิงค์ข้อมูลสินค้าสำเร็จ', 'success');
          
          // ดึงข้อมูลใหม่
          await fetchProducts();
          
        } catch (error: any) {
          console.error('Sync error:', error);
          showAlert('เกิดข้อผิดพลาด', 'ไม่สามารถซิงค์ข้อมูลได้ กรุณาตรวจสอบการเชื่อมต่อ', 'error');
        } finally {
          setIsSyncing(false);
        }
      },
      { confirmText: 'ซิงค์ข้อมูล', cancelText: 'ยกเลิก' }
    );
  };
  
  // ฟังก์ชันสำหรับการเลือกสินค้าทั้งหมด
  // KD3 S2515: เปลี่ยนตัวกรอง/หน้าแล้วต้องล้างการเลือก — ไม่งั้นแถบ "เลือกแล้ว N" ค้าง และ "ลบที่เลือก" ลบแถวที่ผู้ใช้มองไม่เห็นแล้ว
  useEffect(() => {
    setSelectedProducts([]);
  }, [currentPage, pageSize, searchTerm, statusFilter]);

  const handleSelectAll = (selected: boolean) => {
    if (selected) {
      setSelectedProducts(products.map(product => product.id));
    } else {
      setSelectedProducts([]);
    }
  };
  
  // ฟังก์ชันสำหรับการเลือกสินค้าแต่ละรายการ
  const handleSelectProduct = (productId: number, selected: boolean) => {
    if (selected) {
      setSelectedProducts(prev => [...prev, productId]);
    } else {
      setSelectedProducts(prev => prev.filter(id => id !== productId));
    }
  };
  
  // ฟังก์ชันสำหรับการลบสินค้าหลายรายการ
  const handleBulkDelete = async () => {
    if (selectedProducts.length === 0) {
      showAlert('เลือกข้อมูล', 'กรุณาเลือกสินค้าที่ต้องการลบ', 'info');
      return;
    }
    
    showConfirm(
      'ยืนยันการลบสินค้า',
      `คุณต้องการลบสินค้า ${selectedProducts.length} รายการที่เลือก ใช่หรือไม่? การกระทำนี้ไม่สามารถเปลี่ยนกลับได้`,
      async () => {
        setBulkLoading(true);
        try {
          // BUG-S2103-002: allSettled — รายการที่ลบไม่ได้ต้องบอกเหตุผล · ตารางต้อง refetch · เลือกค้างเฉพาะที่ลบไม่ได้
          const result = await runBulkDelete(selectedProducts, id => productService.deleteProduct(id));
          setSelectedProducts(result.failed.map(f => f.id));
          await fetchProducts();
          if (result.failed.length === 0) {
            showAlert('สำเร็จ', `ลบสินค้า ${result.succeeded.length} รายการสำเร็จ`, 'success');
          } else {
            showAlert(result.succeeded.length ? 'ลบได้บางรายการ' : 'ลบไม่สำเร็จ', bulkDeleteFailureMessage('สินค้า', result), result.succeeded.length ? 'warning' : 'error');
          }
        } catch (error) {
          console.error('Error bulk deleting:', error);
          showAlert('เกิดข้อผิดพลาด', 'ไม่สามารถลบรายการที่เลือกได้', 'error');
        } finally {
          setBulkLoading(false);
        }
      },
      { isDanger: true, confirmText: 'ลบ', cancelText: 'ยกเลิก' }
    );
  };

  // คอลัมน์ของตาราง
  const columns = [
    // Checkbox column - แสดงเฉพาะเมื่อมีสิทธิ์ Delete
    ...(canDelete('products') ? [{
      key: 'select',
      title: (
        <div className="flex items-center">
          <input
            type="checkbox"
            aria-label="เลือกทั้งหมด"
            checked={selectedProducts.length === products.length && products.length > 0}
            ref={(input) => { if (input) { input.indeterminate = selectedProducts.length > 0 && selectedProducts.length < products.length; } }}
            onChange={(e) => handleSelectAll(e.target.checked)}
            className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
          />
        </div>
      ),
      width: '50px',
      render: (value: any, record: Product) => (
        <input
          type="checkbox"
          aria-label={`เลือกรายการ ${record.code || record.name || record.id}`}
          checked={selectedProducts.includes(record.id)}
          onChange={(e) => handleSelectProduct(record.id, e.target.checked)}
          className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
        />
      ),
    }] : []),
    {
      key: 'code',
      title: 'รหัสสินค้า',
      sortable: true,
    },
    {
      key: 'name',
      title: 'ชื่อสินค้า',
      sortable: true,
    },
    {
      key: 'monthlyRentalRate',
      title: 'ค่าเช่า/เดือน (บาท)',
      sortable: true,
      render: (value: number) => value ? value.toLocaleString() : '0',
    },
    {
      key: 'depositAmount',
      title: 'เงินมัดจำ (บาท)',
      sortable: true,
      render: (value: number) => value ? value.toLocaleString() : '0',
    },
    {
      key: 'isActive',
      title: 'สถานะ',
      sortable: true,
      render: (value: boolean) => (
        <span
          className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
            value ? 'bg-success-100 text-success-800' : 'bg-danger-100 text-danger-800'
          }`}
        >
          {value ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}
        </span>
      ),
    },
    {
      key: 'syncedFromFlow',
      title: 'Sync จาก Flow',
      sortable: true,
      render: (value: boolean) => (
        <span
          className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
            value ? 'bg-info-100 text-info-800' : 'bg-secondary-100 text-secondary-800'
          }`}
        >
          {value ? 'Sync แล้ว' : 'ยังไม่ Sync'}
        </span>
      ),
    },
    {
      key: 'created_at',
      title: 'วันที่สร้าง',
      sortable: true,
      render: (value: string | null) => formatDateTimeFull(value)
    },
    {
      key: 'updated_at',
      title: 'อัพเดทล่าสุด',
      sortable: true,
      render: (value: string | null) => formatDateTimeFull(value)
    },
    {
      key: 'actions',
      title: 'การดำเนินการ',
      sticky: true,
      render: (_: any, row: Product) => (
        <ActionDropdown
          actions={[
            {
              label: 'ดูรายละเอียด',
              icon: <RiEyeLine className="w-4 h-4" />,
              onClick: () => openProductPage(productDetailUrl(row.id))
            },
            canEdit('products') ? {
              label: 'แก้ไข',
              icon: <RiEditLine className="w-4 h-4" />,
              onClick: () => openProductPage(productEditUrl(row.id))
            } : null,
            (canEdit('products') || canDelete('products')) ? {
              divider: true
            } : null,
            canDelete('products') ? {
              label: 'ลบ',
              icon: <RiDeleteBinLine className="w-4 h-4" />,
              onClick: () => handleDeleteProduct(row),
              className: 'text-danger-600 hover:text-danger-700 hover:bg-danger-50'
            } : null
          ].filter(Boolean)}
          buttonIcon={null}
          buttonLabel=""
        />
      )
    }
  ];
  
  // ส่วนหัวของหน้า
  const renderPageHeader = () => {
    return (
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center">
            <RiArchiveLine className="w-8 h-8 text-primary-600 mr-2" />
            <div>
              <h1 className="text-2xl font-bold text-secondary-900">ข้อมูลสินค้า</h1>
              <p className="mt-1 text-sm text-secondary-500">
                จัดการข้อมูลสินค้าและซิงค์กับ Flow Account
              </p>
            </div>
          </div>
          
          {/* ปุ่ม Sync Flow Account - ซ่อนไว้ชั่วคราว
          <div className="mt-4 sm:mt-0">
            <button
              type="button"
              className="inline-flex items-center px-4 min-h-[44px] sm:min-h-[38px] py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              onClick={handleSyncProducts}
              disabled={isSyncing}
            >
              <RiRefreshLine className={`mr-2 h-5 w-5 ${isSyncing ? 'animate-spin' : ''}`} />
              ซิงค์ข้อมูลกับ Flow Account
            </button>
          </div>
          */}
        </div>
        
      </div>
    );
  };
  
  // ฟังก์ชัน Refresh
  const handleRefresh = () => {
    setSearchTerm('');
    setCurrentPage(1);
    setStatusFilter('');
    setSelectedProducts([]);
    fetchProducts();
  };

  // State สำหรับ Export Loading — parity กับอีก 4 หน้าข้อมูลหลัก
  // หน้านี้เพิ่งได้ตัวกรอง (ไฟล์เล็กลง/เร็วขึ้น) คนจะกดถี่ขึ้น ถ้าไม่มี feedback ว่ากำลังส่งออก
  // ผู้ใช้จะกดรัวแล้วยิง export ซ้อนกันหลายรอบโดยไม่รู้ตัว
  const [isExporting, setIsExporting] = useState(false);

  // ฟังก์ชัน Export Excel
  const handleExportExcel = async () => {
    setIsExporting(true);
    try {
      // ส่งตัวกรองชุดเดียวกับที่หน้ารายการใช้ — ไฟล์ต้องได้เฉพาะแถวที่เห็นบนจอ
      const blob = await productService.exportToExcel({
        search: searchTerm || undefined,
        ...(statusFilter ? { is_active: statusFilter === 'active' } : {}),
      });
      downloadExcelFile(blob, `products_${getLocalDateString()}.xlsx`);
      showAlert('สำเร็จ', 'ส่งออกข้อมูลเรียบร้อยแล้ว', 'success');
    } catch (error) {
      console.error('Error exporting data:', error);
      showAlert('เกิดข้อผิดพลาด', await getApiErrorMessageAsync(error, 'ไม่สามารถส่งออกข้อมูลได้'), 'error');
    } finally {
      setIsExporting(false);
    }
  };

  // Filters configuration สำหรับ SearchFilterCard
  const filters: FilterConfig[] = [
    {
      key: 'status',
      label: 'สถานะ',
      type: 'select',
      options: [
        { value: 'active', label: 'เปิดใช้งาน' },
        { value: 'inactive', label: 'ปิดใช้งาน' },
      ],
      tagColor: 'success',
    },
  ];
  

  return (
    <Layout>
      <div className="w-full">
        {/* Page Header with Executive Navy Theme */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
          <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-primary-600 flex items-center justify-center flex-shrink-0 shadow-sm">
            <RiShoppingBag3Line className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-secondary-900 leading-tight">สินค้า</h1>
            <p className="text-xs text-secondary-500 mt-0.5">จัดการรายการสินค้าและบริการ</p>
          </div>
        </div>
        {totalItems > 0 && (
          <span className="inline-flex items-center px-3 min-h-[44px] sm:min-h-[32px] py-1.5 rounded-lg bg-secondary-100 text-secondary-600 text-xs font-medium self-start sm:self-auto">
            {totalItems.toLocaleString()} รายการ
          </span>
        )}
      </div>

          {/* แสดง Error Message ถ้ามีข้อผิดพลาด */}
          {error && (
            <div className="mb-4 p-4 border border-danger-200 rounded-md bg-danger-50 text-danger-700">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-danger-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm">เกิดข้อผิดพลาดในการโหลดข้อมูล</p>
                  <div className="mt-2">
                    <button
                      type="button"
                      className="text-sm font-medium text-danger-700 hover:text-danger-600 focus:outline-none"
                      onClick={() => fetchProducts()}
                    >
                      ลองใหม่อีกครั้ง
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons Row */}
          <ActionButtonsRow
            onAdd={canCreate('products') ? () => openProductPage(PRODUCT_NEW_URL) : undefined}
            onRefresh={handleRefresh}
            refreshLoading={isLoading}
            onExport={canExport('products') ? handleExportExcel : undefined}
            exportDisabled={products.length === 0}
          />

          {/* Search & Filter Card */}
          <SearchFilterCard
            searchTerm={searchTerm}
            onSearchChange={(value) => {
              setSearchTerm(value);
              setCurrentPage(1);
            }}
            filters={filters}
            filterValues={{ status: statusFilter }}
            onFilterChange={(key, value) => {
              if (key === 'status') {
                setStatusFilter(value);
                setCurrentPage(1);
              }
            }}
            searchPlaceholder="ค้นหารหัสหรือชื่อสินค้า..."
          />

          {/* Bulk Actions Bar */}
          {canDelete('products') && (
            <BulkActionsBar
              selectedCount={selectedProducts.length}
              onBulkDelete={handleBulkDelete}
              onClearSelection={() => setSelectedProducts([])}
              loading={bulkLoading}
            />
          )}

          {/* Table */}
          <Table
            key={`table-${statusFilter}-${currentPage}`}
            columns={columns}
            data={products}
            emptyText={error ? 'โหลดข้อมูลไม่สำเร็จ' : undefined}
            emptyHint={error ? null : undefined}  /* BUG-S2273-001: โหลดล้ม ≠ ตัวกรองไม่เจอ */
            loading={isLoading}
            pagination={false}
            searchable={false}
            sortable={true}
            actions={false}
          />
        
        {/* Pagination */}
        {!isLoading && products.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalItems={totalItems}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={(newPageSize) => {
              setPageSize(newPageSize);
              setCurrentPage(1);
            }}
            showPageSizeSelector={true}
            showSummary={true}
            className="mt-5"
          />
        )}
        
        {/* Loading Overlay for Export */}
        <LoadingOverlayModal
          isOpen={isExporting}
          icon="export"
          title="กำลังส่งออกข้อมูล"
          submessage="กรุณารอสักครู่ ระบบกำลังเตรียมไฟล์ Excel"
        />
      </div>
    </Layout>
  );
};

export default ProductPage;